import { useEffect, useRef, useState } from 'react'
import { useFormik } from 'formik'
import {
  Get_Carpet_Unit,
  Get_Country,
  Get_Property_Type,
  Get_Province,
  getAllKYC,
} from '../Property_Api'
import { Edit_Property_overview } from '../Edit_Propetry/Edit_Propetry_Api'
import Swal from 'sweetalert2'
import { CircularProgress } from '@mui/material'
import {
  countryDataType,
  provinceDataType,
  propertyTypeDataType,
  carpetAreaUnitDataType,
  propertyDetailsDataType,
} from '../PropertyDataTypes'
import { AddPropertyValidationSchema } from '../PropertyValidationSchema'

interface allKYCDataType {
  account_kyc_name: string
  account_kyc_type: string
  account_kyc_uuid: string
  business_name: string
}

const PropertyOverview = ({ overviewDetails, get_view_property }: { overviewDetails: propertyDetailsDataType, get_view_property: () => Promise<void> }) => {
  const formRef = useRef(null)
  const [country, setCountry] = useState<countryDataType[]>()
  const [province, setProvince] = useState<provinceDataType[]>([])
  const [propertyType, setPropertyType] = useState<propertyTypeDataType[]>([])
  const [carpetAreaUnit, setCarpetAreaUnit] = useState<carpetAreaUnitDataType[]>([])
  const [show, setShow] = useState<boolean>(false)
  const [editPropertyLoading, setEditPropertyLoading] = useState<boolean>(false)
  const [allKYC, setAllKYC] = useState<allKYCDataType[]>([])

  // function to get all KYC list
  async function getListAllKYC() {
    try {
      const { data: response } = await getAllKYC()
      if (response.code === '200') {
        setAllKYC(response.data.filter((kyc: any) => kyc.account_kyc_status === 1))
      } else if (response.code === '204') {
        setAllKYC(response.data)
      } else {
        Swal.fire({
          position: 'center',
          icon: 'error',
          title: response.msg.charAt(0).toUpperCase() + response.msg.slice(1),
          text: response.msg_details,
          allowOutsideClick: true,
        })
      }
    } catch (error: any) {
      Swal.fire({
        position: 'center',
        icon: 'error',
        title: error,
      })
    }
  }

  // function to get property type from API
  async function getPropertyType() {
    try {
      const { data: response } = await Get_Property_Type()
      if (response.code === '200') {
        setPropertyType(response.data)
      } else {
        Swal.fire({
          position: 'center',
          icon: 'error',
          title: response.msg.charAt(0).toUpperCase() + response.msg.slice(1),
          text: response.msg_details,
          allowOutsideClick: true,
        })
      }
    } catch (error: any) {
      Swal.fire({
        position: 'center',
        icon: 'error',
        title: error,
      })
    }
  }

  // function to get carpet area unit from API
  async function getCarpetAreaUnit() {
    try {
      const { data: response } = await Get_Carpet_Unit()
      if (response.code === '200') {
        setCarpetAreaUnit(response.data)
      } else {
        Swal.fire({
          position: 'center',
          icon: 'error',
          title: response.msg.charAt(0).toUpperCase() + response.msg.slice(1),
          text: response.msg_details,
          allowOutsideClick: true,
        })
      }
    } catch (error: any) {
      Swal.fire({
        position: 'center',
        icon: 'error',
        title: error,
      })
    }
  }

  // function to get country from API
  async function getCountry() {
    try {
      const { data: response } = await Get_Country()
      if (response.code === '200') {
        setCountry(response.data)
      } else {
        Swal.fire({
          position: 'center',
          icon: 'error',
          title: response.msg.charAt(0).toUpperCase() + response.msg.slice(1),
          text: response.msg_details,
          allowOutsideClick: true,
        })
      }
    } catch (error: any) {
      Swal.fire({
        position: 'center',
        icon: 'error',
        title: error,
      })
    }
  }

  // function to get state from API
  async function getProvince(country: number) {
    try {
      const { data: response } = await Get_Province(country)
      if (response.code === '200') {
        setProvince(response.data)
      } else {
        Swal.fire({
          position: 'center',
          icon: 'error',
          title: response.msg.charAt(0).toUpperCase() + response.msg.slice(1),
          text: response.msg_details,
          allowOutsideClick: true,
        })
      }
    } catch (error: any) {
      Swal.fire({
        position: 'center',
        icon: 'error',
        title: error,
      })
    }
  }

  // fetch country, property type, carpet area onload
  useEffect(() => {
    // setLoading(true)
    const fetchData = async () => {
      try {
        await Promise.all([getCountry(), getPropertyType(), getCarpetAreaUnit(), getListAllKYC()])
      } catch (error: any) {
        Swal.fire({
          position: 'center',
          icon: 'error',
          title: error,
        })
      }
      // finally {
      //   setLoading(false)
      // }
    }
    fetchData()
  }, [])

  // fetch state based on the country
  useEffect(() => {
    if (overviewDetails?.country_id) {
      getProvince(overviewDetails?.country_id)
    }
  }, [overviewDetails?.country_id])

  // formik for edit property form
  const formik = useFormik({
    initialValues: overviewDetails,
    enableReinitialize: true,
    validationSchema: AddPropertyValidationSchema,
    onSubmit: () => handleSubmit(),
  })

  // submit function to edit property
  const handleSubmit = () => {
    formik.setTouched(
      {
        property_type_uuid: true,
        property_title: true,
        carpet_area: true,
        carpet_area_unit_uuid: true,
        address_1: true,
        address_2: true,
        country_id: true,
        province_id: true,
        city: true,
        pin_zip: true,
        current_rent: true,
        property_usage_type: true,
        property_furnishing_status: true,
      },
      true
    )
    formik.validateForm().then(async (error) => {
      if (Object.keys(error).length === 0) {
        setEditPropertyLoading(true)
        try {
          const { data: res } = await Edit_Property_overview(
            overviewDetails.property_uuid,
            formik.values.property_type_uuid,
            formik.values.property_title,
            Number(formik.values.carpet_area),
            formik.values.carpet_area_unit_uuid,
            formik.values.address_1,
            formik.values.address_2,
            Number(formik.values.country_id),
            Number(formik.values.country_id),
            formik.values.city,
            formik.values.pin_zip,
            Number(formik.values.current_rent),
            formik.values.account_kyc_uuid,
            overviewDetails.sub_unit_available,
            Number(formik.values.property_usage_type),
            Number(formik.values.property_furnishing_status),
            overviewDetails.sub_units,
            overviewDetails.inventoryDetails
          )
          if (res.code === '200') {
            Swal.fire({
              position: 'center',
              icon: 'success',
              title: res.msg.charAt(0).toUpperCase() + res.msg.slice(1),
              text: res.msg_details,
              allowOutsideClick: true,
            })
            // getProperty()
            get_view_property()
            setShow(false)
          } else {
            Swal.fire({
              position: 'center',
              icon: 'error',
              title: res.msg.charAt(0).toUpperCase() + res.msg.slice(1),
              text: res.msg_details,
              allowOutsideClick: true,
            })
          }
        }
        catch (error: any) {
          Swal.fire({
            position: 'center',
            icon: 'error',
            title: 'Error',
            text: error,
            allowOutsideClick: true,
          })
        }
        finally {
          setEditPropertyLoading(false)
        }
      } else {
        const firstErrorField = Object.keys(error)[0]
        if (firstErrorField) {
          // @ts-ignore
          const fieldElement = formRef.current?.elements[firstErrorField] as HTMLElement | undefined
          if (fieldElement) {
            fieldElement.scrollIntoView({ behavior: 'smooth' })
          }
        }
      }
    })
  }

  // function to handle change in country
  const handleChangeCountry = (val: string) => {
    getProvince(Number(val))
    formik.setFieldValue('province_id', '', true)
    formik.setFieldValue('country_id', val, true)
  }

  return (
    <>
      {/* Begin: overview details for the property */}
      {!show && overviewDetails && (
        <div className='card mb-6 mb-xl-10' id='kt_profile_details_view'>
          <div className='card-header cursor-pointer'>
            {/* Begin: Heading */}
            <div className='card-title m-0'>
              <h3 className='fw-bold m-0'>Details</h3>
            </div>
            {/* End: Heading */}
            {/* Begin: Edit property button */}
            <button
              className='align-self-center btn btn-sm btn-primary me-3'
              onClick={() => setShow(true)}
            >
              Edit Property
            </button>
            {/* End: Edit property button */}
          </div>
          {/* Begin: property overview content */}
          {editPropertyLoading ? (
            // {/* Begin: Loader */}
            <div
              className='d-flex justify-content-center align-items-center vh-70'
              style={{ height: '70vh' }}
            >
              <CircularProgress />
            </div>
          ) : (
            // {/* End: Loader */}
            <div className='card-body p-9 row'>
              {/* Begin: left column */}
              <div className='col-md-6'>
                {/* Begin: Detail field Property Title */}
                <div className='row mb-6'>
                  <label className='col-lg-6 fw-bold text-muted'>Property Title</label>
                  <div className='col-lg-6'>
                    <span className='fw-bold fs-6 pb-1'>
                      {overviewDetails.property_title.charAt(0).toUpperCase() +
                        overviewDetails.property_title.slice(1)}
                    </span>
                  </div>
                </div>
                {/* End: Detail field Property Title */}
                {/* Begin: Detail field Carpet Area */}
                <div className='row mb-6'>
                  <label className='col-lg-6 fw-bold text-muted'>Carpet Area</label>
                  <div className='col-lg-6 d-flex align-items-center'>
                    <span className='fw-bold fs-6 pb-1 me-2'>
                      {`${overviewDetails.carpet_area} ${overviewDetails.carpet_area_unit_name.toLowerCase() ===
                        'Square Feet'.toLowerCase()
                        ? 'sq. ft.'
                        : 'sq. m.'
                        }`}
                    </span>
                  </div>
                </div>
                {/* End: Detail field Carpet Area */}
                {/* Begin: Detail field Category */}
                <div className='row mb-6'>
                  <label className='col-lg-6 fw-bold text-muted'>Category</label>
                  <div className='col-lg-6'>
                    <span className='fw-bold fs-6 pb-1'>
                      {overviewDetails.property_usage_type === 1 ? 'Residential' : 'Commercial'}
                    </span>
                  </div>
                </div>
                {/* End: Detail field Category */}
                {/* Begin: Detail field Address Line 1 */}
                <div className='row mb-6'>
                  <label className='col-lg-6 fw-bold text-muted'>Address Line 1</label>
                  <div className='col-lg-6'>
                    <span className='fw-bold fs-6 pb-1 text-dark pb-1'>
                      {overviewDetails.address_1}
                    </span>
                  </div>
                </div>
                {/* End: Detail field Address Line 1 */}
                {/* Begin: Detail field City */}
                <div className='row mb-6'>
                  <label className='col-lg-6 fw-bold text-muted'>City</label>
                  <div className='col-lg-6'>
                    <span className='fw-bold fs-6 pb-1'>{overviewDetails.city}</span>
                  </div>
                </div>
                {/* End: Detail field City */}
                {/* Begin: Detail field Country */}
                <div className='row mb-6'>
                  <label className='col-lg-6 fw-bold text-muted'>Country</label>
                  <div className='col-lg-6'>
                    <span className='fw-bold fs-6 pb-1 text-dark pb-1'>
                      {overviewDetails.country_name}
                    </span>
                  </div>
                </div>
                {/* End: Detail field Country */}
                {/* Begin: Detail field Are You an */}
                <div className='row mb-6'>
                  <label className='col-lg-6 fw-bold text-muted'>Are You an</label>
                  <div className='col-lg-6'>
                    <span className='fw-bold fs-6 pb-1'>
                      {overviewDetails.isAgent ? 'Agent' : 'Owner'}
                    </span>
                  </div>
                </div>
                {/* End: Detail field Are You an */}
                {/* Begin: Detail field Owner Phone Number */}
                {overviewDetails.owner_contact_number && (
                  <div className='row mb-6'>
                    <label className='col-lg-6 fw-bold text-muted'>Owner Phone Number</label>
                    <div className='col-lg-6'>
                      <span className='fw-bold fs-6 pb-1'>
                        {overviewDetails.owner_contact_number}
                      </span>
                    </div>
                  </div>
                )}
                {/* End: Detail field Owner Phone Number */}
              </div>
              {/* End: left column */}

              {/* Begin: Right column */}
              <div className='col-md-6'>
                {/* Begin: Detail field Property Type */}
                <div className='row mb-6'>
                  <label className='col-lg-6 fw-bold text-muted'>Property Type</label>
                  <div className='col-lg-6 fv-row'>
                    <span className='fw-bold fs-6 pb-1'>{overviewDetails.property_type_name}</span>
                  </div>
                </div>
                {/* End: Detail field Property Type */}
                {/* Begin: Detail field Expected Rent */}
                <div className='row mb-6'>
                  <label className='col-lg-6 fw-bold text-muted'>Expected Rent</label>
                  <div className='col-lg-6'>
                    <span className='fw-bold fs-6 pb-1'>{`${overviewDetails.currency_code === 'INR' ? '₹' : overviewDetails.currency_code
                      } ${overviewDetails.current_rent}`}</span>
                  </div>
                </div>
                {/* End: Detail field Expected Rent */}
                {/* Begin: Detail field KYC Account */}
                <div className='row mb-6'>
                  <label className='col-lg-6 fw-bold text-muted'>KYC Account</label>
                  <div className='col-lg-6'>
                    <span className='fw-bold fs-6 pb-1'>{overviewDetails.account_kyc_name ? overviewDetails.account_kyc_name : '-'}</span>
                  </div>
                </div>
                {/* End: Detail field KYC Account */}
                {/* Begin: Detail field Furnishing Type */}
                <div className='row mb-6'>
                  <label className='col-lg-6 fw-bold text-muted'>Furnishing Type</label>
                  <div className='col-lg-6'>
                    <span className='fw-bold fs-6 pb-1'>
                      {overviewDetails.property_furnishing_status === 1
                        ? 'Unfurnished'
                        : overviewDetails.property_furnishing_status === 2
                          ? 'Semi Furnished'
                          : overviewDetails.property_furnishing_status === 3
                            ? 'Fully Furnished'
                            : 'not available'}
                    </span>
                  </div>
                </div>
                {/* End: Detail field Furnishing Type */}
                {/* Begin: Detail field Address Line 2 */}
                <div className='row mb-6'>
                  <label className='col-lg-6 fw-bold text-muted'>Address Line 2</label>
                  <div className='col-lg-6'>
                    <span className='fw-bold fs-6 pb-1 text-dark pb-1'>
                      {overviewDetails.address_2 === '' ? '-' : overviewDetails.address_2}
                    </span>
                  </div>
                </div>
                {/* End: Detail field Address Line 2 */}
                {/* Begin: Detail field Province/State */}
                <div className='row mb-6'>
                  <label className='col-lg-6 fw-bold text-muted'>Province/State</label>
                  <div className='col-lg-6'>
                    <span className='fw-bold fs-6 pb-1'>{overviewDetails.province_name}</span>
                  </div>
                </div>
                {/* End: Detail field Province/State */}
                {/* Begin: Detail field PIN/Postcode */}
                <div className='row mb-6'>
                  <label className='col-lg-6 fw-bold text-muted'>PIN/Postcode</label>
                  <div className='col-lg-6'>
                    <span className='fw-bold fs-6 pb-1'>{overviewDetails.pin_zip}</span>
                  </div>
                </div>
                {/* End: Detail field PIN/Postcode */}
                {/* Begin: Detail field Owner Name */}
                {overviewDetails.owner_name && (
                  <div className='row mb-6'>
                    <label className='col-lg-6 fw-bold text-muted'>Owner Name</label>
                    <div className='col-lg-6'>
                      <span className='fw-bold fs-6 pb-1'>{overviewDetails.owner_name}</span>
                    </div>
                  </div>
                )}
                {/* End: Detail field Owner Name */}
                {/* Begin: Detail field Owner Email */}
                {overviewDetails.owner_email_id && (
                  <div className='row mb-6'>
                    <label className='col-lg-6 fw-bold text-muted'>Owner Email</label>
                    <div className='col-lg-6'>
                      <span className='fw-bold fs-6 pb-1'>{overviewDetails.owner_email_id}</span>
                    </div>
                  </div>
                )}
                {/* End: Detail field Owner Email */}
              </div>
              {/* End: right column */}
            </div>
          )}
          {/* Begin: property overview content */}
        </div>
      )}
      {/* End: overview details for the property */}

      {/* Begin: Edit property section */}
      {show && overviewDetails && (
        <div className='card mb-5 mb-xl-10'>
          <div className='card-header border-0 d-flex justify-content-between align-items-center'>
            {/* Begin: heading */}
            <div className='card-title m-0'>
              <h3 className='fw-bolder m-0'>Details</h3>
            </div>
            {/* End: heading */}
            {/* Begin: Cancel and save button */}
            <div>
              <button
                className='btn btn-sm btn-light fw-bold bg-color-white shadow me-4 btn-active-light-primary'
                onClick={() => setShow(false)}
              >
                Cancel
              </button>
              <button
                type='submit'
                className='btn btn-sm btn-primary shadow'
                onClick={handleSubmit}
                disabled={editPropertyLoading}
              >
                {editPropertyLoading ? (
                  <span className='indicator-progress' style={{ display: 'block' }}>
                    Processing...
                    <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                  </span>
                ) : (
                  'Save'
                )}
              </button>
            </div>
            {/* End: Cancel and save button */}
          </div>

          <div id='kt_account_profile_details' className='collapse show'>
            <form ref={formRef} onSubmit={formik.handleSubmit} className='form'>
              <div className='card-body border-top p-9'>
                {/* Begin: form field Property Title */}
                <div className='row mb-6'>
                  <label className='col-lg-4 col-form-label fw-bold fs-6'>
                    <span className='required'>Property Title</span>
                    <i
                      className='fas fa-exclamation-circle ms-2 fs-7'
                      data-bs-toggle='tooltip'
                      title='Specify your property title'
                    ></i>
                  </label>
                  <div className='col-lg-8 fv-row'>
                    <input
                      type='text'
                      className='form-control form-control-lg form-control-solid'
                      placeholder='Property title'
                      {...formik.getFieldProps('property_title')}
                    />
                    {formik.touched.property_title && formik.errors.property_title && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>
                          {formik.errors.property_title.toString()}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {/* End: form field Property Title */}
                {/* Begin: form field Property Type */}
                <div className='row mb-6'>
                  <label className='col-lg-4 col-form-label fw-bold fs-6'>
                    <span className='required'>Property Type</span>
                    <i
                      className='fas fa-exclamation-circle ms-2 fs-7'
                      data-bs-toggle='tooltip'
                      title='Choose your property type'
                    ></i>
                  </label>

                  <div className='col-lg-8 fv-row'>
                    <select
                      className='form-select form-select-solid form-select-lg fw-bold'
                      {...formik.getFieldProps('property_type_uuid')}
                    >
                      <option value='' disabled>
                        Select property type
                      </option>
                      {propertyType?.map((item: propertyTypeDataType) => {
                        return (
                          <option key={item.property_type_id} value={item.property_type_uuid}>
                            {item.property_type_name}
                          </option>
                        )
                      })}
                    </select>
                    {formik.touched.property_type_uuid && formik.errors.property_type_uuid && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>
                          {formik.errors.property_type_uuid.toString()}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {/* End: form field Property Type */}
                {/* Begin: form field Carpet Area */}
                <div className='row mb-6'>
                  <label className='col-lg-4 col-form-label fw-bold fs-6'>
                    <span className='required'>Carpet Area</span>
                    <i
                      className='fas fa-exclamation-circle ms-2 fs-7'
                      data-bs-toggle='tooltip'
                      title='Specify carpet area of property'
                    ></i>
                  </label>

                  <div className='col-lg-8 fv-row'>
                    <input
                      type='text'
                      className='form-control form-control-lg form-control-solid'
                      placeholder='Carpet area'
                      {...formik.getFieldProps('carpet_area')}
                    />
                    {formik.touched.carpet_area && formik.errors.carpet_area && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>{formik.errors.carpet_area.toString()}</div>
                      </div>
                    )}
                  </div>
                </div>
                {/* End: form field Carpet Area */}
                {/* Begin: form field Area Unit */}
                <div className='row mb-6'>
                  <label className='col-lg-4 col-form-label fw-bold fs-6'>
                    <span className='required'>Area Unit</span>
                    <i
                      className='fas fa-exclamation-circle ms-2 fs-7'
                      data-bs-toggle='tooltip'
                      title='Choose unit for carpet area'
                    ></i>
                  </label>
                  <div className='col-lg-8 fv-row'>
                    <div className='row'>
                      {/*begin:Option */}
                      <label className='d-flex col-12 col-lg-6 flex-fill align-items-center justify-content-between cursor-pointer'>
                        <span className='d-flex align-items-center me-2'>
                          <span className='symbol symbol-50px me-2'>
                            <span className='symbol-label bg-light-primary'>
                              <p className='mb-0 display-7 font-weight-bold'>ft²</p>
                            </span>
                          </span>

                          <span className='d-flex flex-column'>
                            <span className='fw-bolder fs-6'>
                              {carpetAreaUnit[0]?.carpet_area_unit_name}
                            </span>
                          </span>
                        </span>

                        <span className='form-check form-check-custom form-check-solid'>
                          <input
                            className='form-check-input'
                            {...formik.getFieldProps('carpet_area_unit_uuid')}
                            name='carpet_area_unit_uuid'
                            type='radio'
                            value={carpetAreaUnit[0]?.carpet_area_unit_uuid}
                            id='carpet_area_unit_uuid'
                            defaultChecked={
                              carpetAreaUnit[0]?.carpet_area_unit_uuid ===
                              overviewDetails?.carpet_area_unit_uuid
                            }
                          />
                        </span>
                      </label>
                      {/*end::Option */}

                      {/*begin:Option */}
                      <label className='d-flex col-12 col-lg-6 flex-fill align-items-center justify-content-between cursor-pointer'>
                        <span className='d-flex align-items-center me-2'>
                          <span className='symbol symbol-50px me-2'>
                            <span className='symbol-label bg-light-danger'>
                              <p className='mb-0 display-7 font-weight-bold'>m²</p>
                            </span>
                          </span>

                          <span className='d-flex flex-column'>
                            <span className='fw-bolder fs-6'>
                              {carpetAreaUnit[1]?.carpet_area_unit_name}
                            </span>
                          </span>
                        </span>

                        <span className='form-check form-check-custom form-check-solid'>
                          <input
                            className='form-check-input'
                            {...formik.getFieldProps('carpet_area_unit_uuid')}
                            name='carpet_area_unit_uuid'
                            type='radio'
                            value={carpetAreaUnit[1]?.carpet_area_unit_uuid}
                            id='carpet_area_unit_uuid'
                            defaultChecked={
                              carpetAreaUnit[1]?.carpet_area_unit_uuid ===
                              overviewDetails?.carpet_area_unit_uuid
                            }
                          />
                        </span>
                      </label>
                      {/*end::Option */}
                    </div>
                    <div className='h-20px'>
                      {formik.touched.carpet_area_unit_uuid &&
                        formik.errors.carpet_area_unit_uuid && (
                          <div className='fv-help-block color-red'>
                            {formik.errors.carpet_area_unit_uuid.toString()}
                          </div>
                        )}
                    </div>
                  </div>
                </div>
                {/* End: form field Area Unit */}
                {/* Begin: form field Address Line 1 */}
                <div className='row mb-6'>
                  <label className='col-lg-4 col-form-label fw-bold fs-6'>
                    <span className='required'>Address Line 1</span>
                    <i
                      className='fas fa-exclamation-circle ms-2 fs-7'
                      data-bs-toggle='tooltip'
                      title='Enter complete address'
                    ></i>
                  </label>

                  <div className='col-lg-8 fv-row'>
                    <input
                      type='text'
                      className='form-control form-control-lg form-control-solid'
                      placeholder='Address Line 1'
                      {...formik.getFieldProps('address_1')}
                    />
                    {formik.touched.address_1 && formik.errors.address_1 && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>{formik.errors.address_1.toString()}</div>
                      </div>
                    )}
                  </div>
                </div>
                {/* End: form field Address Line 1 */}
                {/* Begin: form field Address Line 2 */}
                <div className='row mb-6'>
                  <label className='col-lg-4 col-form-label fw-bold fs-6'>
                    <span className=''>Address Line 2</span>
                    <i
                      className='fas fa-exclamation-circle ms-2 fs-7'
                      data-bs-toggle='tooltip'
                      title='Enter complete address'
                    ></i>
                  </label>

                  <div className='col-lg-8 fv-row'>
                    <input
                      type='text'
                      className='form-control form-control-lg form-control-solid'
                      placeholder='Address Line 2'
                      {...formik.getFieldProps('address_2')}
                    />
                    {formik.touched.address_2 && formik.errors.address_2 && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>{formik.errors.address_2.toString()}</div>
                      </div>
                    )}
                  </div>
                </div>
                {/* End: form field Address Line 2 */}
                {/* Begin: form field Country */}
                <div className='row mb-6'>
                  <label className='col-lg-4 col-form-label fw-bold fs-6'>
                    <span className='required'>Country</span>
                    <i
                      className='fas fa-exclamation-circle ms-2 fs-7'
                      data-bs-toggle='tooltip'
                      title='Choose your country'
                    ></i>
                  </label>

                  <div className='col-lg-8 fv-row'>
                    <select
                      className='form-select form-select-solid form-select-lg fw-bold'
                      {...formik.getFieldProps('country_id')}
                      onChange={(e) => handleChangeCountry(e.target.value)}
                    >
                      <option value='' disabled>
                        Select a Country
                      </option>
                      {country?.map((item) => {
                        return (
                          <option key={item.country_id} value={item.country_id}>
                            {item.country_name}
                          </option>
                        )
                      })}
                    </select>
                    {formik.touched.country_id && formik.errors.country_id && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>{formik.errors.country_id.toString()}</div>
                      </div>
                    )}
                  </div>
                </div>
                {/* End: form field Country */}
                {/* Begin: form field Province/State */}
                <div className='row mb-6'>
                  <label className='col-lg-4 col-form-label fw-bold fs-6'>
                    <span className='required'>Province/State</span>
                    <i
                      className='fas fa-exclamation-circle ms-2 fs-7'
                      data-bs-toggle='tooltip'
                      title='Choose your province/state'
                    ></i>
                  </label>

                  <div className='col-lg-8 fv-row'>
                    <select
                      className='form-select form-select-solid form-select-lg fw-bold'
                      {...formik.getFieldProps('province_id')}
                    >
                      <option value='' disabled>
                        Select a Province/State
                      </option>
                      {province.map((item) => {
                        return (
                          <option key={item.province_id} value={item.province_id}>
                            {item.province_name}
                          </option>
                        )
                      })}
                    </select>
                    {formik.touched.province_id && formik.errors.province_id && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>{formik.errors.province_id.toString()}</div>
                      </div>
                    )}
                  </div>
                </div>
                {/* End: form field Province/State */}
                {/* Begin: form field City */}
                <div className='row mb-6'>
                  <label className='col-lg-4 col-form-label fw-bold fs-6'>
                    <span className='required'>City</span>
                    <i
                      className='fas fa-exclamation-circle ms-2 fs-7'
                      data-bs-toggle='tooltip'
                      title='Enter city name'
                    ></i>
                  </label>
                  <div className='col-lg-8 fv-row'>
                    <input
                      type='text'
                      className='form-control form-control-lg form-control-solid'
                      placeholder='City'
                      {...formik.getFieldProps('city')}
                    />
                    {formik.touched.city && formik.errors.city && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>{formik.errors.city.toString()}</div>
                      </div>
                    )}
                  </div>
                </div>
                {/* End: form field City */}
                {/* Begin: form field PIN/Postcode */}
                <div className='row mb-6'>
                  <label className='col-lg-4 col-form-label fw-bold fs-6'>
                    <span className='required'>PIN/Postcode</span>
                    <i
                      className='fas fa-exclamation-circle ms-2 fs-7'
                      data-bs-toggle='tooltip'
                      title='Enter pincode'
                    ></i>
                  </label>
                  <div className='col-lg-8 fv-row'>
                    <input
                      type='text'
                      className='form-control form-control-lg form-control-solid'
                      placeholder='PIN/Postcode'
                      {...formik.getFieldProps('pin_zip')}
                    />
                    {formik.touched.pin_zip && formik.errors.pin_zip && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>{formik.errors.pin_zip.toString()}</div>
                      </div>
                    )}
                  </div>
                </div>
                {/* End: form field PIN/Postcode */}
                {/* Begin: form field Expected Rent */}
                {
                  (overviewDetails?.sub_units &&
                    overviewDetails?.sub_units.length <= 0) &&
                  <div className='row mb-6'>
                    <label className='col-lg-4 col-form-label fw-bold fs-6'>
                      <span className='required'>Expected Rent</span>
                      <i
                        className='fas fa-exclamation-circle ms-2 fs-7'
                        data-bs-toggle='tooltip'
                        title='Enter expected rent'
                      ></i>
                    </label>
                    <div className='col-lg-8 fv-row'>
                      <input
                        type='text'
                        className='form-control form-control-lg form-control-solid'
                        placeholder='Total expected rent'
                        {...formik.getFieldProps('current_rent')}
                      />
                      {formik.touched.current_rent && formik.errors.current_rent && (
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block'>{formik.errors.current_rent.toString()}</div>
                        </div>
                      )}
                    </div>
                  </div>
                }
                {/* End: form field Expected Rent */}
                {/* Begin: form field KYC Account */}
                {allKYC.length > 0 &&
                  <div className='row mb-6'>
                  <label className='col-lg-4 col-form-label fw-bold fs-6'>
                    <span>KYC Account</span>
                    <i
                      className='fas fa-exclamation-circle ms-2 fs-7'
                      data-bs-toggle='tooltip'
                      title='Choose KYC account'
                    ></i>
                  </label>

                  <div className='col-lg-8 fv-row'>
                    <select
                      className='form-select form-select-solid form-select-lg fw-bold'
                      {...formik.getFieldProps('account_kyc_uuid')}
                    >
                      <option value=''>Select KYC account</option>
                      {allKYC?.map((item: allKYCDataType) => {
                        return (
                          <option key={item.account_kyc_uuid} value={item.account_kyc_uuid}>
                            {item.account_kyc_name}
                          </option>
                        )
                      })}
                    </select>
                    {formik.touched.account_kyc_uuid && formik.errors.account_kyc_uuid && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>
                          {formik.errors.account_kyc_uuid.toString()}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                }
                {/* End: form field KYC Account */}
                {/* Begin: form field Furnishing Type */}
                <div className='row mb-6'>
                  <label className='col-lg-4 col-form-label fw-bold fs-6'>
                    <span className='required'>Furnishing Type</span>
                    <i
                      className='fas fa-exclamation-circle ms-2 fs-7'
                      data-bs-toggle='tooltip'
                      title='Choose furnishing type'
                    ></i>
                  </label>

                  <div className='col-lg-8 fv-row'>
                    <select
                      className='form-select form-select-solid form-select-lg fw-bold'
                      {...formik.getFieldProps('property_furnishing_status')}
                    >
                      <option value='' disabled>
                        Select furnishing type
                      </option>
                      <option value='1'>Unfurnished</option>
                      <option value='2'>Semi Furnished</option>
                      <option value='3'>Fully Furnished</option>
                    </select>
                    {formik.touched.property_furnishing_status &&
                      formik.errors.property_furnishing_status && (
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block'>
                            {formik.errors.property_furnishing_status.toString()}
                          </div>
                        </div>
                      )}
                  </div>
                </div>
                {/* End: form field Furnishing Type */}
                {/* Begin: form field Category */}
                <div className='row mb-6'>
                  <label className='col-lg-4 col-form-label fw-bold fs-6'>
                    <span className='required'>Category</span>
                    <i
                      className='fas fa-exclamation-circle ms-2 fs-7'
                      data-bs-toggle='tooltip'
                      title='Enter property category'
                    ></i>
                  </label>

                  <div className='col-lg-8 fv-row'>
                    <div className='row'>
                      {/*begin:Option */}
                      <label className='col-lg-6 col-12 d-flex flex-basis-50 align-items-center justify-content-between cursor-pointer'>
                        <span className='d-flex align-items-center me-2'>
                          <span className='symbol symbol-50px me-2'>
                            <span className='symbol-label bg-light-primary'>
                              <i className='bi bi-house-door fs-2x'></i>
                            </span>
                          </span>

                          <span className='d-flex flex-column'>
                            <span className='fw-bolder fs-6'>Residential</span>
                          </span>
                        </span>

                        <span className='form-check form-check-custom form-check-solid'>
                          <input
                            className='form-check-input'
                            {...formik.getFieldProps('property_usage_type')}
                            name='property_usage_type'
                            type='radio'
                            value='1'
                            id='property_usage_type'
                            defaultChecked={overviewDetails?.property_usage_type === 1}
                          />
                        </span>
                      </label>
                      {/*end::Option */}

                      {/*begin:Option */}
                      <label className='col-lg-6 col-12 d-flex flex-basis-50 align-items-center justify-content-between cursor-pointer'>
                        <span className='d-flex align-items-center me-2'>
                          <span className='symbol symbol-50px me-2'>
                            <span className='symbol-label bg-light-danger'>
                              <i className='bi bi-building fs-2x'></i>
                            </span>
                          </span>

                          <span className='d-flex flex-column'>
                            <span className='fw-bolder fs-6'>Commercial</span>
                          </span>
                        </span>

                        <span className='form-check form-check-custom form-check-solid'>
                          <input
                            className='form-check-input'
                            {...formik.getFieldProps('property_usage_type')}
                            name='property_usage_type'
                            type='radio'
                            value='2'
                            id='property_usage_type'
                            defaultChecked={overviewDetails?.property_usage_type === 2}
                          />
                        </span>
                      </label>
                      {/*end::Option */}
                    </div>
                    <div className='h-20px'>
                      {formik.touched.property_usage_type && formik.errors.property_usage_type && (
                        <div className='fv-help-block color-red'>
                          {formik.errors.property_usage_type.toString()}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {/* End: form field Category */}
                {/* Begin: cancel and submit button */}
                <div className='card-footer d-flex justify-content-end py-6 px-3'>
                  <button
                    className='btn btn-sm btn-light fw-bold bg-color-white shadow me-4 btn-active-light-primary'
                    onClick={() => setShow(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type='submit'
                    className={`btn btn-sm btn-primary shadow`}
                    disabled={editPropertyLoading}
                  >
                    {editPropertyLoading ? (
                      <span className='indicator-progress' style={{ display: 'block' }}>
                        Processing...
                        <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                      </span>
                    ) : (
                      'Save'
                    )}
                  </button>
                </div>
                {/* End: cancel and submit button */}
              </div>
            </form>
          </div>
        </div>
      )}
      {/* End: Edit property section */}
    </>
  )
}

export default PropertyOverview
